import React, { useState, useRef } from 'react';
import Header from "./partials/Header";
import "../css/Signup.css"
import { useNavigate } from "react-router-dom";
import GoogleMapsLoader from "./partials/GoogleMapsLoader";
import PlaceAutocompleteInput from "./partials/PlaceAutocompleteInput";
import AccountIntro from './partials/AccountIntro';
import RegistrationLegalAcceptance from './RegistrationLegalAcceptance';
import { TERMS_VERSION, PRIVACY_VERSION } from '../legal/legalDocuments';
import { setAuthCookie } from './utils/cookies';
import GoogleBusinessFields from './partials/GoogleBusinessFields';
import { getGoogleBusinessInputError } from './utils/googleBusinessInput';

export default function Signup() {
    const API_AUTH_URL = process.env.REACT_APP_AUTH_API_URL;

    const navigate = useNavigate();
    const placeInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        vat: '',
        hasFuneralHome: 'si',
        location: {
            address: '',
            lat: null,
            lng: null,
            placeId: ''
        },
        description: '',
        since: '',
        vehicles: [''],
        googleBusinessUrl: '',
        googlePlaceId: '',
    });

    const [errors, setErrors] = useState({});
    const [photos, setPhotos] = useState([]);
    const [profilePhotoId, setProfilePhotoId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accountCreated, setAccountCreated] = useState(false);
    const [legalAcceptance, setLegalAcceptance] = useState({
        termsAccepted: false,
        privacyAcknowledged: false,
        professionalConfirmed: false,
    });

    const handleLegalAcceptanceChange = (name, checked) => {
        setLegalAcceptance(prev => ({ ...prev, [name]: checked }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const allLegalRequirementsAccepted = Object.values(legalAcceptance).every(Boolean);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handlePlaceSelected = (place) => {
        setFormData(prev => ({
            ...prev,
            location: {
                address: place.address,
                lat: place.lat,
                lng: place.lng,
                placeId: place.placeId
            }
        }));

        if (errors.location) {
            setErrors(prev => ({
                ...prev,
                location: ''
            }));
        }
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        const maxPhotos = 5; // 1 logo + 4 foto

        if (photos.length + files.length > maxPhotos) {
            alert(`Puoi caricare massimo ${maxPhotos} immagini`);
            return;
        }

        files.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const newPhoto = {
                        id: `${Date.now()}-${Math.random()}`,
                        url: e.target.result,
                        file: file,
                    };
                    setPhotos(prev => {
                        const updated = [...prev, newPhoto];
                        // Se è la prima foto, impostala come profilo
                        if (updated.length === 1) {
                            setProfilePhotoId(newPhoto.id);
                        }
                        return updated;
                    });
                };
                reader.readAsDataURL(file);
            }
        });
    };

    const removePhoto = (photoId) => {
        setPhotos(prev => {
            const filtered = prev.filter(photo => photo.id !== photoId);
            // Se rimuovo la foto profilo, imposta la prima rimanente come profilo
            if (photoId === profilePhotoId && filtered.length > 0) {
                setProfilePhotoId(filtered[0].id);
            } else if (filtered.length === 0) {
                setProfilePhotoId(null);
            }
            return filtered;
        });
    };

    const setAsProfilePhoto = (photoId) => {
        setProfilePhotoId(photoId);
    };

    const handleVehicleChange = (index, value) => {
        const newVehicles = [...formData.vehicles];
        newVehicles[index] = value;
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };

    const addVehicle = () => {
        setFormData(prev => ({ ...prev, vehicles: [...prev.vehicles, ''] }));
    };

    const removeVehicle = (index) => {
        if (formData.vehicles.length > 1) {
            const newVehicles = formData.vehicles.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, vehicles: newVehicles }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading || accountCreated) return;
        const newErrors = {};

        if (!legalAcceptance.termsAccepted) newErrors.termsAccepted = 'Leggi e accetta le condizioni di servizio per iscriverti.';
        if (!legalAcceptance.privacyAcknowledged) newErrors.privacyAcknowledged = 'Conferma di aver letto l’informativa sul trattamento dei dati personali.';
        if (!legalAcceptance.professionalConfirmed) newErrors.professionalConfirmed = 'Conferma di essere maggiorenne e autorizzato a rappresentare l’agenzia.';

        const isAlphanumeric = (text) => {
            return /^[a-zA-Z0-9]{4,45}$/.test(text);
        };

        if (!formData.name) newErrors.name = 'Campo obbligatorio';
        if (!formData.username) newErrors.username = 'Campo obbligatorio';
        if (!isAlphanumeric(formData.username)) newErrors.username = 'Valore non valido';
        if (!formData.email) newErrors.email = 'Campo obbligatorio';
        if (!formData.password) newErrors.password = 'Campo obbligatorio';
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Le password non coincidono';
        }
        if (!formData.phone) newErrors.phone = 'Campo obbligatorio';
        if (!formData.vat) newErrors.vat = 'Campo obbligatorio';
        const googleBusinessError = getGoogleBusinessInputError(formData);
        if (googleBusinessError) newErrors.googleBusiness = googleBusinessError;

        if (!formData.location.address.trim() || formData.location.address.length > 255
            || !Number.isFinite(formData.location.lat) || Math.abs(formData.location.lat) > 90
            || !Number.isFinite(formData.location.lng) || Math.abs(formData.location.lng) > 180
            || (placeInputRef.current && placeInputRef.current.value !== formData.location.address)) {
            newErrors.location = 'Seleziona un indirizzo valido dai suggerimenti Google Maps.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setLoading(true);

        try {
            const submitData = new FormData();

            submitData.append('email', formData.email);
            submitData.append('username', formData.username);
            submitData.append('password', formData.password);
            submitData.append('phone', formData.phone);
            submitData.append('vat', formData.vat);
            submitData.append('funeralHonorTitle', formData.name);
            submitData.append('funeralHonorDescription', formData.description);
            submitData.append('funeralHonorSince', formData.since);
            submitData.append('funeralHonorFuneralHomeAvailable', formData.hasFuneralHome === 'si');
            submitData.append('funeralHonorAddress', formData.location.address);
            submitData.append('funeralHonorLat', formData.location.lat);
            submitData.append('funeralHonorLng', formData.location.lng);
            submitData.append('funeralHonorPlaceId', formData.location.placeId);
            submitData.append('funeralHonorGoogleBusinessUrl', formData.googleBusinessUrl.trim());
            submitData.append('funeralHonorGooglePlaceId', formData.googlePlaceId.trim());
            submitData.append('termsAccepted', 'true');
            submitData.append('termsVersion', TERMS_VERSION);
            submitData.append('privacyAcknowledged', 'true');
            submitData.append('privacyVersion', PRIVACY_VERSION);
            submitData.append('professionalConfirmed', 'true');

            const filteredVehicles = formData.vehicles.filter(v => v.trim() !== '');
            filteredVehicles.forEach(vehicle => {
                submitData.append('funeralHonorVehicles[]', vehicle);
            });

            photos.forEach((photo, index) => {
                submitData.append(`photoOrder[${index}][url]`, photo.url);
                submitData.append(`photoOrder[${index}][isLogo]`, photo.id === profilePhotoId ? 1 : 0);
            });

            photos
                .filter(photo => photo.file)
                .forEach(photo => {
                    submitData.append('photos[]', photo.file);
                });

            const response = await fetch(`${API_AUTH_URL}/auth/funeralHonorSignup`, {
                method: 'POST',
                body: submitData
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    // A server or proxy may return an empty or non-JSON error body.
                }
                const detail = typeof errorData?.detail === 'string' ? errorData.detail : null;
                const message = typeof errorData?.message === 'string' ? errorData.message : null;
                throw new Error(detail || message || (response.status === 422
                    ? 'Uno o più parametri non sono validi'
                    : 'Errore nella registrazione'));
            }
            setAccountCreated(true);
        } catch (error) {
            setErrors({ general: error.message || "Errore nella creazione dell'account" });
            setLoading(false);
            return;
        }

        try {
            const credentials = btoa(`${formData.username}:${formData.password}`);

            const response = await fetch(
                `${API_AUTH_URL}/auth/login`,
                {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Basic ${credentials}`,
                    },
                }
            );

            if (response.status !== 200) {
                throw new Error('Errore nel login.');
            }

            if (!response.ok) {
                throw new Error('Errore del server');
            }

            // Ottiene header Authorization
            const authHeader = response.headers.get('Authorization');
            if (!authHeader || !authHeader.startsWith('Bearer')) {
                throw new Error('Errore nella risposta');
            }

            const token = authHeader.replace('Bearer ', '');

            // Crea cookie MMAUTH
            setAuthCookie(token);

            // Redirect dopo login
            navigate("/profilo");
            window.scrollTo(0, 0);
        } catch (error) {
            setErrors({
                general: 'Account creato correttamente. L’accesso automatico non è riuscito: usa il collegamento “Accedi alla tua area” qui sotto.'
            });
        } finally {
            setLoading(false);
        }
    };

    const addressInput = (
        <input
            id="signup-location"
            type="text"
            placeholder="Es. Via Esempio, 123 Roma"
            value={formData.location.address}
            onChange={(e) => setFormData(prev => ({
                ...prev,
                location: { address: e.target.value, lat: null, lng: null, placeId: '' }
            }))}
            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
        />
    );

    return (
        <div className="ef-page ef-auth-page ef-account-page ef-registration-page min-h-screen">
            <Header />
            <AccountIntro title="La tua agenzia, più vicina alle persone.">Entra nella rete Easy Funeral. Presenta i tuoi servizi e aiuta le famiglie a trovare il supporto di cui hanno bisogno.</AccountIntro>

            <form onSubmit={handleSubmit}>
                <div className="max-w-2xl mx-auto signup-form-background border-radius md:shadow-2xl p-6 space-y-7 my-4 mb-6">
                    {errors.general && <p role="alert" className="ef-account-notice ef-account-notice--error">{errors.general}</p>}
                    <div className="ef-form-section-heading"><span>01</span><div><h2>Agenzia e accesso</h2><p>I tuoi recapiti e le credenziali per l’area riservata.</p></div></div>
                    <div className="ef-registration-grid">
                    <div>
                        <label htmlFor="signup-name" className="block font-bold mb-2">
                            Nome onoranza funebre
                        </label>
                        <input
                            type="text"
                            name="name"
                            id="signup-name"
                            autoComplete="organization"
                            placeholder="Es. Onoranze Funebri X"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-username" className="block font-bold mb-2">
                            Username
                        </label>
                        <input
                            type="text"
                            name="username"
                            id="signup-username"
                            autoComplete="username"
                            autoCapitalize="none"
                            placeholder="Es. onoranzax"
                            value={formData.username}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        <p className="text-xs mt-1">
                            * Minimo 4 caratteri, solo alfanumerici
                        </p>
                        {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-email" className="block font-bold mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            id="signup-email"
                            autoComplete="email"
                            placeholder="Es. indirizzo@mail.it"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-password" className="block font-bold mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            name="password"
                            id="signup-password"
                            autoComplete="new-password"
                            placeholder="Inserisci la password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-confirm-password" className="block font-bold mb-2">
                            Ripeti password
                        </label>
                        <input
                            type="password"
                            name="confirmPassword"
                            id="signup-confirm-password"
                            autoComplete="new-password"
                            placeholder="Inserisci nuovamente la password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-phone" className="block font-bold mb-2">
                            Cellulare
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            id="signup-phone"
                            autoComplete="tel"
                            placeholder="Es. +39 3330000000"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-vat" className="block font-bold mb-2">
                            Partita Iva
                        </label>
                        <input
                            type="text"
                            name="vat"
                            id="signup-vat"
                            placeholder="Es. 02000000000"
                            value={formData.vat}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.vat && <p className="text-red-500 text-xs mt-1">{errors.vat}</p>}
                    </div>

                    <div>
                        <label htmlFor="signup-since" className="block font-bold mb-2">
                            Anno fondazione
                        </label>
                        <input
                            type="number"
                            name="since"
                            id="signup-since"
                            placeholder="Anno (es. 2020)"
                            value={formData.since}
                            onChange={handleInputChange}
                            max={new Date().getFullYear()}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                        {errors.since && <p className="text-red-500 text-xs mt-1">{errors.since}</p>}
                    </div>

                    </div>
                    <div className="ef-form-section-heading"><span>02</span><div><h2>Sede e servizi</h2><p>Aiuta le famiglie a conoscere la tua realtà.</p></div></div>
                    <div>
                        <label className="block font-bold mb-3">
                            Hai a disposizione una casa funeraria?
                        </label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, hasFuneralHome: 'si' }))}
                                className={`px-8 py-2 border-radius transition ${
                                    formData.hasFuneralHome === 'si'
                                        ? 'bg-primary text-white shadow-xl'
                                        : 'bg-gray-200 hover:bg-gray-400'
                                }`}
                            >
                                Si
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, hasFuneralHome: 'no' }))}
                                className={`px-8 py-2 border-radius transition ${
                                    formData.hasFuneralHome === 'no'
                                        ? 'bg-primary text-white shadow-xl'
                                        : 'bg-gray-200 hover:bg-gray-400'
                                }`}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="signup-location" className="block font-bold mb-2">
                            Inserisci la posizione del tuo ufficio principale
                        </label>

                        <GoogleMapsLoader fallback={addressInput}>
                            <PlaceAutocompleteInput
                                id="signup-location"
                                name="location"
                                placeholder="Es. Via Esempio, 123 Roma"
                                description="Inserisci la posizione del tuo ufficio principale"
                                variant="signup"
                                disabled={loading || accountCreated}
                                value={formData.location.address}
                                fallback={addressInput}
                                onReady={element => { placeInputRef.current = element; }}
                                onInput={address => setFormData(prev => ({
                                    ...prev,
                                    location: { address, lat: null, lng: null, placeId: '' }
                                }))}
                                onPlaceSelect={handlePlaceSelected}
                                onError={() => setErrors(prev => ({ ...prev, location: 'I suggerimenti non sono disponibili. Riprova più tardi.' }))}
                            />
                        </GoogleMapsLoader>

                        {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}

                        <p className="text-xs mt-2">
                            * Seleziona un indirizzo dalla lista per garantirne la precisione
                        </p>
                        <p className="text-xs mt-5">
                            Contattare l'assistenza al numero <b>80012345</b> per aggiungere altre sedi
                        </p>
                    </div>

                    <div className="ef-form-section-heading"><span>03</span><div><h2>Racconta la tua agenzia</h2><p>Completa il profilo con immagini, descrizione e parco auto.</p></div></div>
                    <section aria-labelledby="signup-google-business-title">
                        <h3 id="signup-google-business-title" className="block font-bold mb-3">Recensioni Google</h3>
                        <GoogleBusinessFields googleBusinessUrl={formData.googleBusinessUrl} googlePlaceId={formData.googlePlaceId}
                            disabled={loading || accountCreated} error={errors.googleBusiness}
                            onChange={value => { setFormData(prev => ({ ...prev, ...value })); setErrors(prev => ({ ...prev, googleBusiness: '' })); }} />
                    </section>
                    <div>
                        <label className="block font-bold text-gray-700 mb-2">
                            Immagini (max 5)
                        </label>

                        {photos.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                {photos.map((photo) => (
                                    <div key={photo.id} className="relative">
                                        <img
                                            src={photo.url}
                                            alt={photo.name}
                                            className={`w-full h-32 object-cover border-radius border-2`}
                                        />

                                        {photo.id === profilePhotoId && (
                                            <div className="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded-full shadow-lg">
                                                LOGO
                                            </div>
                                        )}

                                        <div className="absolute top-1 right-1 flex gap-1">
                                            {photo.id !== profilePhotoId && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAsProfilePhoto(photo.id)}
                                                    title="Imposta come logo"
                                                    className="bg-primary text-white rounded-full p-1.5 shadow-lg hover:bg-secondary transition"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                                                    </svg>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removePhoto(photo.id)}
                                                title="Rimuovi foto"
                                                className="bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600 transition"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 640 640"><path fill="#ffffff" d="M504.6 148.5C515.9 134.9 514.1 114.7 500.5 103.4C486.9 92.1 466.7 93.9 455.4 107.5L320 270L184.6 107.5C173.3 93.9 153.1 92.1 139.5 103.4C125.9 114.7 124.1 134.9 135.4 148.5L278.3 320L135.4 491.5C124.1 505.1 125.9 525.3 139.5 536.6C153.1 547.9 173.3 546.1 184.6 532.5L320 370L455.4 532.5C466.7 546.1 486.9 547.9 500.5 536.6C514.1 525.3 515.9 505.1 504.6 491.5L361.7 320L504.6 148.5z"/></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <input
                            type="file"
                            id="photo-upload"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                        />
                        <label
                            htmlFor="photo-upload"
                            className={`w-full px-4 py-3 border-radius transition flex items-center justify-center gap-2 cursor-pointer ${
                                photos.length >= 5
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 640 640"><path fill="#000000" d="M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z"/></svg>
                            {photos.length === 0
                                ? 'Aggiungi Foto'
                                : `Aggiungi Foto (${photos.length}/5)`}
                        </label>
                        <p className="text-xs mt-1">
                            Carica fino a 5 immagini. La prima foto caricata sarà la principale, puoi cambiarla cliccando sulla stella.
                        </p>
                    </div>

                    <div>
                        <label className="block font-bold mb-2">
                            Descrizione
                        </label>
                        <textarea
                            name="description"
                            placeholder="Scrivi una breve descrizione"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                        />
                    </div>

                    <div>
                        <label className="block font-bold mb-2">
                            Parco Auto
                        </label>
                        <div className="space-y-2">
                            {formData.vehicles.map((vehicle, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Es. Mercedes Classe E W214 Nero"
                                        value={vehicle}
                                        onChange={(e) => handleVehicleChange(index, e.target.value)}
                                        className="flex-1 px-4 py-3 bg-gray-200 border-radius focus:outline-none focus:ring-2 focus:ring-primary transition"
                                    />
                                    {formData.vehicles.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeVehicle(index)}
                                            className="px-3 py-2 bg-red-500 text-white border-radius hover:bg-red-600 transition"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 640 640"><path fill="#ffffff" d="M504.6 148.5C515.9 134.9 514.1 114.7 500.5 103.4C486.9 92.1 466.7 93.9 455.4 107.5L320 270L184.6 107.5C173.3 93.9 153.1 92.1 139.5 103.4C125.9 114.7 124.1 134.9 135.4 148.5L278.3 320L135.4 491.5C124.1 505.1 125.9 525.3 139.5 536.6C153.1 547.9 173.3 546.1 184.6 532.5L320 370L455.4 532.5C466.7 546.1 486.9 547.9 500.5 536.6C514.1 525.3 515.9 505.1 504.6 491.5L361.7 320L504.6 148.5z"/></svg>
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addVehicle}
                                className="w-full px-4 py-3 bg-green-700 text-white border-radius hover:bg-green-800 transition flex items-center justify-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 640 640"><path fill="#ffffff" d="M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z"/></svg>
                                Aggiungi
                            </button>
                        </div>
                    </div>

                    <RegistrationLegalAcceptance
                        values={legalAcceptance}
                        errors={errors}
                        onChange={handleLegalAcceptanceChange}
                        disabled={loading || accountCreated}
                    />
                <button
                    type="submit"
                    disabled={loading || accountCreated || !allLegalRequirementsAccepted}
                    className={`bg-primary w-full text-white py-4 text-lg ${
                        loading || accountCreated || !allLegalRequirementsAccepted ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                    {loading ? 'Registrazione in corso...' : accountCreated ? 'Account creato' : 'Registrati'}
                </button>
                <p className="text-center">Hai già un account? <a href="/login">Accedi alla tua area →</a></p>
                </div>
            </form>
        </div>
    );
}
